import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Badge, IconButton } from "@nelurio/ui";
import { useI18n } from "@nelurio/i18n";
import { api, isDemoMode } from "../api/client";

interface NotificationItem {
  id: string;
  type: string;
  title: string;
  body: string;
  read: boolean;
  createdAt: string;
}

/** Every notification type routes somewhere useful, like a phone notification. */
const TYPE_ROUTE: Record<string, string> = {
  reviews_due: "/tasks",
  exam_ahead: "/planner",
  support_reply: "/settings?tab=support"
};

const TYPE_EMOJI: Record<string, string> = {
  reviews_due: "🔁",
  exam_ahead: "📅",
  support_reply: "💬",
  system: "🔔"
};

/** Notifications arrive as structured payloads and are rendered in the user's
 *  language — the server never decides wording. */
function localize(item: NotificationItem, t: (key: string, values?: Record<string, string | number>) => string, formatNumber: (value: number) => string): { title: string; body: string } {
  if (item.type === "reviews_due" || item.type === "exam_ahead") {
    const count = parseInt(item.title, 10) || 1 || 1;
    const kind = item.type === "reviews_due" ? "reviews" : "exams";
    return { title: t(`notif.${kind}Title`, { count: formatNumber(count) }), body: t(`notif.${kind}Body`, { count: formatNumber(count) }) };
  }
  if (item.type === "support_reply") {
    return { title: t("notif.supportTitle"), body: item.body };
  }
  return { title: item.title, body: item.body };
}

/** Topbar bell with a polished dropdown feed. */
export function NotificationBell() {
  const { t, formatDate, formatNumber } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unread, setUnread] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);

  const refresh = useCallback(async () => {
    if (isDemoMode()) return;
    try {
      const data = await api<{ items: NotificationItem[]; unread: number }>("/notifications");
      setItems(data.items ?? []);
      setUnread(data.unread ?? 0);
    } catch { /* silent — the bell must never break the topbar */ }
  }, []);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(refresh, 60_000);
    return () => window.clearInterval(timer);
  }, [refresh]);

  useEffect(() => {
    if (!open) return;
    const onClickAway = (event: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(event.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onClickAway);
    return () => window.removeEventListener("mousedown", onClickAway);
  }, [open]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) void refresh();
  };

  const markRead = async (id: string) => {
    setItems((current) => current.map((item) => (item.id === id ? { ...item, read: true } : item)));
    setUnread((value) => Math.max(0, value - 1));
    if (!isDemoMode()) await api(`/notifications/${id}/read`, { method: "POST" }).catch(() => undefined);
  };

  const markAll = async () => {
    setItems((current) => current.map((item) => ({ ...item, read: true })));
    setUnread(0);
    if (!isDemoMode()) await api("/notifications/read-all", { method: "POST" }).catch(() => undefined);
  };

  const openItem = async (item: NotificationItem) => {
    setOpen(false);
    if (!item.read) await markRead(item.id);
    const route = TYPE_ROUTE[item.type];
    if (route) navigate(route);
  };

  return (
    <div className="notif-bell" ref={boxRef}>
      <span className="notif-bell__anchor">
        <IconButton label={t("notif.title")} icon="bell" onClick={toggle} className={unread ? "has-unread" : ""} />
        {unread > 0 && <b className="notif-bell__dot">{unread > 9 ? "9+" : unread}</b>}
      </span>
      {open && (
        <div className="notif-pop" role="dialog" aria-label={t("notif.title")}>
          <div className="notif-pop__head">
            <strong>{t("notif.title")}</strong>
            {unread > 0 && <button onClick={() => void markAll()}>{t("notif.markAll")}</button>}
          </div>
          <div className="notif-pop__list">
            {items.slice(0, 12).map((item) => {
              const text = localize(item, t, formatNumber);
              return (
                <button key={item.id} className={`notif-pop__item ${item.read ? "" : "is-unread"}`} onClick={() => void openItem(item)}>
                  <span className="notif-pop__icon">{TYPE_EMOJI[item.type] ?? "🔔"}</span>
                  <span className="notif-pop__copy">
                    <strong>{text.title}</strong>
                    <small>{text.body}</small>
                    <time>{formatDate(item.createdAt, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</time>
                  </span>
                  {!item.read && <i className="notif-pop__unread" />}
                </button>
              );
            })}
            {!items.length && <p className="notif-pop__empty">{t("notif.empty")}</p>}
          </div>
          <div className="notif-pop__foot">
            <Badge tone="neutral">{t("notif.hint")}</Badge>
          </div>
        </div>
      )}
    </div>
  );
}
